import { NavLink } from 'react-router-dom';

import { BrandMark, BrandName } from '../components/Brand';
import { Tip } from '../components/ui/tooltip';
import { useAuth } from '../features/auth/AuthContext';
import { cn } from '../lib/utils';
import { filterNavItems, type NavItem } from './nav';

export function SidebarNav({
  items: navItems,
  onNavigate,
  collapsed = false,
  label = 'Sections',
}: {
  items: NavItem[];
  onNavigate?: () => void;
  collapsed?: boolean;
  /** Accessible name for this nav landmark — must be unique on the page
   * (the shell renders two SidebarNavs). Also shown as a visible heading
   * above the list when expanded; hidden (icons only) when collapsed,
   * where there's no room for it. */
  label?: string;
}) {
  const { can } = useAuth();
  const items = filterNavItems(navItems, can);
  if (items.length === 0) return null;

  return (
    <div>
      {!collapsed && (
        <p aria-hidden className="label-micro px-6 pb-1.5">
          {label}
        </p>
      )}
      <nav className="flex flex-col gap-0.5 px-3" aria-label={label}>
        {items.map(({ to, label: itemLabel, icon: Icon }) => (
          // The tooltip is anchored to a wrapper rather than to the NavLink
          // itself. Radix's `asChild` merges a child's props by joining them
          // as strings, which turns React Router's function `className` form
          // into its own *source text* — silently replacing every class on the
          // item. It only bit while collapsed, because that's the only state
          // that renders a tooltip at all.
          <Tip key={to} label={collapsed ? itemLabel : undefined} side="right">
            <span className="block">
              <NavLink
                to={to}
                end={to === '/'}
                onClick={onNavigate}
                className={({ isActive }) =>
                  cn(
                    'group relative flex items-center overflow-hidden rounded-[var(--radius-control)]',
                    'py-2 text-sm transition-colors duration-150',
                    // Collapsed rows carry no text, so they'd otherwise be a
                    // line-height shorter than expanded ones; the extra padding
                    // keeps both states on the same vertical rhythm.
                    collapsed ? 'justify-center px-2 py-2.5' : 'gap-3 px-3',
                    isActive
                      ? 'bg-primary-muted text-foreground'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    {/* A soft wash emanating from the accent bar, on top of the
                        flat active background — the only place in the nav the
                        "living glow" language from the login screen shows up. */}
                    {isActive && (
                      <span
                        aria-hidden
                        className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_left,color-mix(in_oklab,var(--primary)_18%,transparent),transparent_70%)]"
                      />
                    )}
                    {/* Live-state marker: the only place the accent glows. */}
                    <span
                      aria-hidden
                      className={cn(
                        'absolute left-0 h-5 w-0.5 rounded-full transition-all duration-200',
                        isActive ? 'bg-primary glow-active' : 'bg-transparent',
                      )}
                    />
                    <Icon
                      className={cn(
                        'relative size-4 shrink-0',
                        isActive ? 'text-primary' : 'text-current',
                      )}
                    />
                    {/* Kept in the DOM (not unmounted) so the link's accessible name
                        survives collapsing — only its visibility changes. */}
                    <span
                      className={cn('relative', isActive && 'font-medium', collapsed && 'sr-only')}
                    >
                      {itemLabel}
                    </span>
                  </>
                )}
              </NavLink>
            </span>
          </Tip>
        ))}
      </nav>
    </div>
  );
}

export function SidebarBrand({ collapsed = false }: { collapsed?: boolean }) {
  return (
    <div
      className={cn('flex items-center gap-2.5 py-5', collapsed ? 'justify-center px-2' : 'px-6')}
    >
      <BrandMark className="size-8" />
      <BrandName className={cn('text-lg', collapsed && 'sr-only')} />
    </div>
  );
}
